import {
  CSRF_HEADER,
  DOMAIN_SIGN_IN_MODES,
  type DomainSignInMode,
  type DomainSignInModePreview,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useAccount } from "../account/queries.ts";
import { StepUpDialog } from "../account/StepUpDialog.tsx";
import { ApiError, needsReauth, ok } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { adminSettingsPath, Link } from "../router.tsx";
import { useSecureAction } from "../security/step-up.tsx";
import { useSession } from "../session.tsx";
import { settingHash } from "../settings-shell/setting-anchors.ts";
import { useAdminApi } from "./admin-api.tsx";
import { adminKeys } from "./entity-tree.ts";
import { signinKeys } from "./signin-keys.ts";

/** A verified domain bound to an active IdP, as its mode dialog needs it. */
export interface ModeTarget {
  id: string;
  domain: string;
  /** The IdP's display name ("Google Workspace"). */
  provider: string;
  signInMode: DomainSignInMode;
}

/** The refusal the preview and the save share (#419, D8). */
type Refusal = NonNullable<DomainSignInModePreview["refusal"]>;
const REFUSALS: readonly Refusal[] = ["last_sign_in_method"];

const refusalOf = (error: unknown): Refusal | null =>
  error instanceof ApiError && REFUSALS.includes(error.code as Refusal)
    ? (error.code as Refusal)
    : null;

/**
 * Who may also use an ESI password or passkey on a domain bound to an IdP (openspec changes
 * `sso-and-esi-credentials`, D1, D7, D8, and `two-domain-sign-in-modes`, #438): the two modes
 * with their help ("SSO or ESI password" first, the default), the tenant's `auth.mfa` that ESI
 * credentials follow (Q8), and, for the mode picked, the server's preview: whether the guard
 * allows it and the sessions it ends. Saving needs a recent sign-in (`reauth_required` runs the
 * step-up).
 */
export function DomainModeDialog(props: {
  target: ModeTarget | null;
  onClose: () => void;
  onDone: (notice: string) => void;
  returnFocus?: () => HTMLElement | null;
}) {
  // The last target stays shown while the dialog fades out.
  const last = useRef<ModeTarget | null>(null);
  if (props.target) {
    last.current = props.target;
  }
  const target = props.target ?? last.current;
  return (
    <AppDialog
      open={props.target !== null}
      onClose={props.onClose}
      returnFocus={props.returnFocus}
      title={target ? t("signin.mode.title", { domain: target.domain }) : ""}
      description={target ? t("signin.mode.lead", { name: target.provider }) : undefined}
    >
      {target ? <ModeBody key={target.id} {...props} target={target} /> : null}
    </AppDialog>
  );
}

function ModeBody(props: {
  target: ModeTarget;
  onClose: () => void;
  onDone: (notice: string) => void;
}) {
  const { target } = props;
  useTranslation();
  const id = useId();
  const { admin, base } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const account = useAccount();
  const stepUp = useSecureAction();
  const [mode, setMode] = useState<DomainSignInMode>(target.signInMode);
  const changed = mode !== target.signInMode;

  // The tenant's own `auth.mfa` (entities may require more): what ESI credentials follow.
  const catalogue = useQuery({
    queryKey: adminKeys.catalogue(null),
    queryFn: async () => ok(await admin.settings.catalogue.$get({ query: {} })),
    retry: false,
  });
  const mfa = catalogue.data?.settings.find((s) => s.key === "auth.mfa")?.value;

  const preview = useQuery({
    queryKey: ["admin", "sign-in-mode-preview", target.id, mode],
    queryFn: async (): Promise<DomainSignInModePreview> =>
      (await ok(
        await admin.domains[":domainId"]["sign-in-mode"].preview.$get({
          param: { domainId: target.id },
          query: { mode },
        }),
      )) as DomainSignInModePreview,
    // The stored mode needs no preview: nothing would change.
    enabled: changed,
    retry: false,
    // A snapshot for this choice only (each one reads the directory): never an older one.
    gcTime: 0,
    staleTime: 0,
  });

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await admin.domains[":domainId"]["sign-in-mode"].$put(
          { param: { domainId: target.id }, json: { mode } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSettled: () =>
      Promise.all(
        [signinKeys.identityProviders, ["admin", "audit"], ["admin", "users"]].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });

  const submit = () =>
    stepUp
      .secure(async () => {
        const result = await save.mutateAsync();
        const label = t(`signin.mode.labels.${result.mode}`);
        props.onDone(
          result.sessions > 0
            ? `${t("signin.mode.saved", { domain: target.domain, mode: label })} ${t("signin.mode.savedSessions", { count: result.sessions })}`
            : t("signin.mode.saved", { domain: target.domain, mode: label }),
        );
      })
      // Refusals stay on the mutation, shown in place.
      .catch(() => undefined);

  const current = changed ? preview.data : undefined;
  // A save refused after a fresh preview (someone changed something meanwhile) wins.
  const refusal = refusalOf(save.error) ?? (current?.allowed === false ? current.refusal : null);
  const otherError =
    save.error && !refusalOf(save.error) && !needsReauth(save.error) ? save.error : null;
  const waiting = stepUp.asking && account.isPending;
  const ready = changed && current?.allowed === true && !refusal;
  const busy = save.isPending;

  return (
    <>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) {
            void submit();
          }
        }}
      >
        <RadioGroup
          value={mode}
          onValueChange={(v) => {
            save.reset();
            setMode(v as DomainSignInMode);
          }}
          aria-label={t("signin.mode.choose", { domain: target.domain })}
          className="gap-2"
        >
          {DOMAIN_SIGN_IN_MODES.map((m) => (
            <label
              key={m}
              htmlFor={`${id}-${m}`}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-(--duration-fast)",
                // Selected in ink, never the accent (DESIGN.md, the Ink, Not Accent Rule).
                mode === m
                  ? "border-fg bg-surface-raised ring-1 ring-fg"
                  : "border-line hover:bg-surface-hover",
              )}
            >
              <RadioGroupItem
                id={`${id}-${m}`}
                value={m}
                className="mt-0.5"
                aria-describedby={`${id}-${m}-help`}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg">
                  {t(`signin.mode.labels.${m}`)}
                  {m === target.signInMode ? (
                    <span className="font-normal text-fg-muted"> {t("signin.mode.current")}</span>
                  ) : null}
                </span>
                <span id={`${id}-${m}-help`} className="text-xs leading-relaxed text-fg-secondary">
                  {t(`signin.mode.help.${m}`, { name: target.provider })}
                </span>
              </span>
            </label>
          ))}
        </RadioGroup>

        <Notice tone="info">
          {mfa === "required"
            ? t("signin.mode.mfaRequired")
            : mfa === "optional"
              ? t("signin.mode.mfaOptional")
              : t("signin.mode.mfaUnknown")}{" "}
          <Link
            href={`${adminSettingsPath("security", base)}${settingHash("auth.mfa")}`}
            className="font-medium underline underline-offset-4"
          >
            {t("signin.mode.mfaLink")}
          </Link>
        </Notice>

        {refusal ? (
          <Notice tone="danger">
            <span className="font-medium text-danger-fg">
              {t(`signin.mode.refused.${refusal}.title`)}
            </span>{" "}
            {t(`signin.mode.refused.${refusal}.body`, { name: target.provider })}
          </Notice>
        ) : changed && preview.isError ? (
          <Notice tone="danger">{preview.error.message}</Notice>
        ) : current && current.people > 0 ? (
          <Notice tone="warning">
            {t("signin.mode.impact", {
              people: t("signin.mode.people", { count: current.people }),
              sessions: t("signin.removal.sessions", { count: current.sessions }),
              grants: t("signin.removal.grants", { count: current.grants }),
            })}
          </Notice>
        ) : null}

        {otherError ? <Notice tone="danger">{otherError.message}</Notice> : null}
        {stepUp.asking && account.isError ? (
          <Notice tone="danger">{account.error.message}</Notice>
        ) : null}
        {changed && !refusal ? (
          <p className="text-xs text-fg-muted">{t("signin.removal.recentSignIn")}</p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!ready || busy || waiting || (changed && preview.isFetching)}
          >
            {busy ? t("signin.mode.saving") : t("signin.mode.save")}
          </Button>
        </DialogFooter>
      </form>
      <StepUpDialog
        // Only once the account is known: a federated admin must never get the password form.
        open={stepUp.asking && account.isSuccess}
        passkeys={account.data?.security.passkeysAvailable ?? false}
        federatedEmail={account.data?.federated ? account.data.email : null}
        returnTo={`${base}/signin`}
        onClose={stepUp.cancel}
        onDone={() => {
          const action = stepUp.take();
          if (action) {
            void stepUp.secure(action).catch(() => undefined);
          }
        }}
      />
    </>
  );
}
